import React, { useState, useMemo, useRef, useEffect } from "react";
import { AppLayout, PageHeader } from "./_shared/AppLayout";
import { useAppData } from "./_shared/store";
import { brl, fmtDate } from "./_shared/utils";
import { toast } from "sonner";
import {
  ShoppingCart, ShoppingBag, Plus, Trash2, Check, X, Copy,
  CheckCircle2, Pencil, Share2, FileText, MessageCircle
} from "lucide-react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const today = () => new Date().toISOString().slice(0, 10);

const listaTotal = (lista) =>
  (lista.itens || []).reduce((s, it) => s + Number(it.qtd || 1) * Number(it.valorUnit || 0), 0);

const listaCheckedTotal = (lista) =>
  (lista.itens || [])
    .filter((it) => it.checked)
    .reduce((s, it) => s + Number(it.qtd || 1) * Number(it.valorUnit || 0), 0);

// ─── Share logic ────────────────────────────────────────────────
const generateShareText = (lista) => {
  const items = lista.itens || [];
  let text = `🛒 *${lista.nome.toUpperCase()}*\n`;
  text += `📅 ${fmtDate(lista.data)} · ${items.length} itens · ${brl(listaTotal(lista))}\n\n`;
  items.forEach(it => {
    const marker = it.checked ? "✅" : "⬜";
    text += `${marker} ${it.desc} (${it.qtd}) - ${brl(it.valorUnit * it.qtd)}\n`;
  });
  text += `\n💰 *Total: ${brl(listaTotal(lista))}*\n`;
  text += `Enviado pelo Monyxx Finance`;
  return text;
};

const shareWhatsApp = (lista) => {
  const text = generateShareText(lista);
  window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
};

const shareCopy = (lista) => {
  const text = generateShareText(lista);
  navigator.clipboard.writeText(text);
  toast.success("Lista copiada!");
};

const sharePDF = (lista) => {
  const doc = new jsPDF();
  doc.setFontSize(16);
  doc.text("Monyxx Finance", 14, 15);
  doc.setFontSize(14);
  doc.text(`Lista: ${lista.nome}`, 14, 25);
  doc.setFontSize(10);
  doc.text(`Data: ${fmtDate(lista.data)}`, 14, 32);

  const items = lista.itens || [];
  const body = items.map(it => [
    it.desc,
    it.qtd,
    brl(it.valorUnit),
    brl(it.qtd * it.valorUnit),
    it.checked ? "Sim" : "Não"
  ]);

  autoTable(doc, {
    head: [["Item", "Qtd", "Valor Unit.", "Subtotal", "Marcado"]],
    body: body,
    startY: 38,
    styles: { fontSize: 9 },
    headStyles: { fillColor: [15, 23, 42], textColor: [255, 255, 255] },
    alternateRowStyles: { fillColor: [241, 245, 249] } // Corrigido para dark mode opcional, ou vamos deixar default. 
    // Na verdade, Monyxx usa modo dark no layout, podemos usar cores dark no pdf se quisermos.
  });

  const finalY = doc.lastAutoTable.finalY || 40;
  doc.setFontSize(12);
  doc.text(`Total estimado: ${brl(listaTotal(lista))}`, 14, finalY + 10);
  doc.save(`lista_${lista.nome}.pdf`);
  toast.success("PDF gerado!");
};

function ShareModal({ lista, onClose }) {
  if (!lista) return null;
  return (
    <div className="fixed inset-0 z-[60] grid place-items-center p-4 bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <div className="w-full max-w-sm bg-slate-900 border border-slate-700 rounded-2xl p-5 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-4">
          <div className="text-base font-bold text-white">Compartilhar Lista</div>
          <button onClick={onClose} className="h-8 w-8 grid place-items-center rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="text-sm text-slate-400 mb-5 leading-relaxed">
          Escolha como deseja compartilhar a lista <span className="text-emerald-400 font-semibold">{lista.nome}</span>
        </div>
        <div className="space-y-3">
          <button onClick={() => { shareWhatsApp(lista); onClose(); }} className="w-full h-12 flex items-center gap-3 px-4 rounded-xl bg-[#25D366]/10 text-[#25D366] border border-[#25D366]/20 hover:bg-[#25D366]/20 transition">
            <MessageCircle className="h-5 w-5" />
            <span className="font-semibold">WhatsApp</span>
          </button>
          <button onClick={() => { sharePDF(lista); onClose(); }} className="w-full h-12 flex items-center gap-3 px-4 rounded-xl bg-rose-500/10 text-rose-400 border border-rose-500/20 hover:bg-rose-500/20 transition">
            <FileText className="h-5 w-5" />
            <span className="font-semibold">Exportar PDF</span>
          </button>
          <button onClick={() => { shareCopy(lista); onClose(); }} className="w-full h-12 flex items-center gap-3 px-4 rounded-xl bg-blue-500/10 text-blue-400 border border-blue-500/20 hover:bg-blue-500/20 transition">
            <Copy className="h-5 w-5" />
            <span className="font-semibold">Copiar Texto</span>
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Overview card ────────────────────────────────────────────
function OverviewCard({ lista, onClick, onShare }) {
  const total = listaTotal(lista);
  const items = lista.itens || [];
  const checkedCount = items.filter((it) => it.checked).length;
  const isFinished = lista.status === "finalizada";

  return (
    <button
      onClick={onClick}
      className="w-full text-left rounded-2xl bg-slate-900 border border-slate-800 hover:border-slate-700 p-4 transition"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className={`h-10 w-10 rounded-xl grid place-items-center shrink-0 ${isFinished ? "bg-emerald-500/20" : "bg-amber-500/20"}`}>
            {isFinished
              ? <CheckCircle2 className="h-5 w-5 text-emerald-400" />
              : <ShoppingCart className="h-5 w-5 text-amber-400" />}
          </div>
          <div className="min-w-0">
            <div className="font-semibold text-white truncate">{lista.nome}</div>
            <div className="text-xs text-slate-400 mt-0.5">
              {fmtDate(lista.data)} · {items.length} {items.length === 1 ? "item" : "itens"}
            </div>
          </div>
        </div>
        <div className="text-right shrink-0">
          <div className="flex items-center gap-3 justify-end">
            <button
              onClick={(e) => { e.stopPropagation(); onShare(); }}
              className="h-7 w-7 grid place-items-center rounded-lg text-slate-400 hover:text-blue-400 hover:bg-slate-800 transition"
              title="Compartilhar"
            >
              <Share2 className="h-4 w-4" />
            </button>
            <div className={`font-bold tabular-nums ${isFinished ? "text-emerald-300" : "text-white"}`}>{brl(total)}</div>
          </div>
          <div className={`text-xs mt-0.5 ${isFinished ? "text-emerald-400" : "text-slate-400"}`}>
            {isFinished ? "Finalizada" : `${checkedCount}/${items.length} marcados`}
          </div>
        </div>
      </div>
      {!isFinished && items.length > 0 && (
        <div className="mt-3">
          <div className="h-1.5 rounded-full bg-slate-800 overflow-hidden">
            <div
              className="h-full bg-amber-400 rounded-full transition-all"
              style={{ width: `${items.length > 0 ? (checkedCount / items.length) * 100 : 0}%` }}
            />
          </div>
        </div>
      )}
    </button>
  );
}

// ─── Nova lista dialog ─────────────────────────────────────────
function NovaListaDialog({ onClose, onCreate }) {
  const [nome, setNome] = useState("");
  const [data, setData] = useState(today());
  const inputRef = useRef(null);

  useEffect(() => { inputRef.current?.focus(); }, []);

  const submit = (e) => {
    e.preventDefault();
    if (!nome.trim()) return;
    onCreate({ nome: nome.trim(), data });
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center p-4 bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <div className="w-full max-w-sm bg-slate-900 border border-slate-700 rounded-2xl p-5 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="text-base font-bold mb-4">Nova Lista de Compras</div>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <label className="text-xs text-slate-400 mb-1.5 block">Nome da lista</label>
            <input
              ref={inputRef}
              value={nome}
              onChange={(e) => setNome(e.target.value)}
              placeholder="Ex: Supermercado semanal"
              className="w-full h-10 px-3 rounded-xl bg-slate-800 border border-slate-700 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-amber-500/40"
            />
          </div>
          <div>
            <label className="text-xs text-slate-400 mb-1.5 block">Data</label>
            <input
              type="date"
              value={data}
              onChange={(e) => setData(e.target.value)}
              className="w-full h-10 px-3 rounded-xl bg-slate-800 border border-slate-700 text-sm text-white focus:outline-none focus:border-amber-500/40"
            />
          </div>
          <div className="flex gap-2 pt-1">
            <button type="button" onClick={onClose} className="flex-1 h-10 rounded-xl border border-slate-700 text-sm text-slate-300 hover:bg-slate-800 transition">
              Cancelar
            </button>
            <button type="submit" disabled={!nome.trim()} className="flex-1 h-10 rounded-xl bg-amber-500 hover:bg-amber-400 disabled:opacity-40 text-sm font-semibold text-slate-950 transition">
              Criar
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Item row ─────────────────────────────────────────────────
function ItemRow({ item, isFinished, onToggle, onDelete, onUpdate }) {
  const [editing, setEditing] = useState(false);
  const [desc, setDesc] = useState(item.desc);
  const [qtd, setQtd] = useState(String(item.qtd));
  const [valorUnit, setValorUnit] = useState(String(item.valorUnit));

  const save = () => {
    onUpdate({ desc: desc.trim() || item.desc, qtd: Number(qtd) || 1, valorUnit: Number(valorUnit) || 0 });
    setEditing(false);
  };

  const subtotal = Number(item.qtd || 1) * Number(item.valorUnit || 0);

  if (editing) {
    return (
      <div className="px-4 py-3 bg-slate-800/50">
        <div className="flex gap-2 items-center">
          <input
            value={desc}
            onChange={(e) => setDesc(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") save(); if (e.key === "Escape") setEditing(false); }}
            className="flex-1 h-9 px-2.5 rounded-lg bg-slate-800 border border-slate-700 text-sm text-white focus:outline-none focus:border-amber-500/40"
            autoFocus
          />
          <input
            value={qtd}
            onChange={(e) => setQtd(e.target.value)}
            type="number"
            min="0.1"
            step="any"
            className="w-14 h-9 px-2 text-center rounded-lg bg-slate-800 border border-slate-700 text-sm text-white focus:outline-none"
          />
          <input
            value={valorUnit}
            onChange={(e) => setValorUnit(e.target.value)}
            type="number"
            min="0"
            step="any"
            placeholder="R$"
            className="w-20 h-9 px-2 text-center rounded-lg bg-slate-800 border border-slate-700 text-sm text-white focus:outline-none"
          />
          <button onClick={save} className="h-9 w-9 grid place-items-center rounded-lg bg-emerald-500/20 text-emerald-300 hover:bg-emerald-500/30 transition">
            <Check className="h-4 w-4" />
          </button>
          <button onClick={() => setEditing(false)} className="h-9 w-9 grid place-items-center rounded-lg bg-slate-700/50 text-slate-400 hover:bg-slate-700 transition">
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`px-3 py-3 flex items-center gap-2 hover:bg-slate-900/40 transition group ${item.checked ? "opacity-60" : ""}`}>
      <button
        onClick={onToggle}
        disabled={isFinished}
        className={`h-5 w-5 rounded-md border-2 grid place-items-center shrink-0 transition ${item.checked ? "bg-amber-400 border-amber-400" : "border-slate-600 bg-transparent"}`}
      >
        {item.checked && (
          <svg viewBox="0 0 12 10" className="h-3 w-3">
            <path d="M1 5 L4.5 8.5 L11 1" stroke="#1e293b" strokeWidth="2" fill="none" strokeLinecap="round" />
          </svg>
        )}
      </button>
      <div className="flex-1 min-w-0">
        <div className={`text-sm font-medium truncate ${item.checked ? "line-through text-slate-400" : "text-white"}`}>
          {item.desc}
        </div>
        <div className="text-[11px] text-slate-400">
          {item.qtd}x · {brl(item.valorUnit)} un
        </div>
      </div>
      <div className="text-sm font-semibold tabular-nums text-white shrink-0">{brl(subtotal)}</div>
      {!isFinished && (
        <div className="flex items-center gap-1 md:opacity-0 md:group-hover:opacity-100 transition shrink-0">
          <button
            onClick={() => setEditing(true)}
            className="h-8 w-8 grid place-items-center rounded-lg text-slate-400 hover:text-amber-300 hover:bg-slate-800 transition"
            title="Editar"
          >
            <Pencil className="h-3.5 w-3.5" />
          </button>
          <button
            onClick={onDelete}
            className="h-8 w-8 grid place-items-center rounded-lg text-slate-400 hover:text-rose-300 hover:bg-slate-800 transition"
            title="Remover"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Main ─────────────────────────────────────────────────────
const ListaDeCompras = () => {
  const {
    data,
    addLista, removeLista,
    addItemLista, updateItemLista, removeItemLista,
    finalizarLista, addLancamento,
  } = useAppData();

  const listas = data.listas || [];

  const [view, setView] = useState("overview");
  const [selectedId, setSelectedId] = useState(null);
  const [tab, setTab] = useState("abertas");
  const [showNova, setShowNova] = useState(false);
  const [shareLista, setShareLista] = useState(null);

  const [itemDesc, setItemDesc] = useState("");
  const [itemQtd, setItemQtd] = useState("1");
  const [itemValor, setItemValor] = useState("");

  const [showFinalizar, setShowFinalizar] = useState(false);
  const [finalizarPagamento, setFinalizarPagamento] = useState("Débito");

  const itemInputRef = useRef(null);

  const selectedLista = useMemo(() => listas.find((l) => l.id === selectedId), [listas, selectedId]);

  const suggestions = useMemo(() => {
    if (itemDesc.trim().length < 2) return [];
    const term = itemDesc.toLowerCase();
    const seen = new Set();
    const results = [];
    for (const lista of listas) {
      for (const it of (lista.itens || [])) {
        const key = it.desc.toLowerCase();
        if (key.includes(term) && !seen.has(key)) {
          seen.add(key);
          results.push(it);
          if (results.length >= 5) return results;
        }
      }
    }
    return results;
  }, [listas, itemDesc]);

  const openDetail = (id) => {
    setSelectedId(id);
    setView("detail");
    setItemDesc("");
    setItemQtd("1");
    setItemValor("");
  };

  const goBack = () => {
    setView("overview");
    setSelectedId(null);
  };

  const handleCreateLista = (fields) => {
    const nova = addLista(fields);
    openDetail(nova.id);
    toast.success(`Lista "${fields.nome}" criada!`);
  };

  const handleAddItem = (e) => {
    e.preventDefault();
    if (!itemDesc.trim() || !selectedId) return;
    addItemLista(selectedId, {
      desc: itemDesc.trim(),
      qtd: Math.max(0.1, Number(itemQtd) || 1),
      valorUnit: Number(itemValor) || 0,
    });
    setItemDesc("");
    setItemQtd("1");
    setItemValor("");
    itemInputRef.current?.focus();
  };

  const handleSuggestion = (sug) => {
    setItemDesc(sug.desc);
    setItemQtd(String(sug.qtd || 1));
    setItemValor(String(sug.valorUnit || ""));
    itemInputRef.current?.focus();
  };

  const handleFinalizar = () => {
    if (!selectedLista) return;
    const total = listaTotal(selectedLista);
    const novo = addLancamento("variavel", {
      desc: selectedLista.nome,
      valor: total,
      cat: "mercado",
      pagamento: finalizarPagamento,
      data: today(),
      pago: true,
      nota: `Lista de compras: ${(selectedLista.itens || []).length} itens`,
    });
    finalizarLista(selectedId, novo.id);
    setShowFinalizar(false);
    toast.success("Compra finalizada! Gasto variável registrado.");
    goBack();
  };

  const handleDuplicar = (lista) => {
    const nova = addLista({ nome: `${lista.nome} (cópia)`, data: today() });
    (lista.itens || []).forEach(({ id: _id, ...rest }) => {
      addItemLista(nova.id, { ...rest, checked: false });
    });
    openDetail(nova.id);
    toast.success("Lista duplicada!");
  };

  const abertas = listas.filter((l) => l.status !== "finalizada");
  const finalizadas = listas.filter((l) => l.status === "finalizada");

  // ─── Detail view ────────────────────────────────────────────
  if (view === "detail" && selectedLista) {
    const total = listaTotal(selectedLista);
    const checkedTotal = listaCheckedTotal(selectedLista);
    const items = selectedLista.itens || [];
    const checkedCount = items.filter((it) => it.checked).length;
    const isFinished = selectedLista.status === "finalizada";

    return (
      <AppLayout>
        <PageHeader
          title={selectedLista.nome}
          subtitle={`${items.length} itens · ${fmtDate(selectedLista.data)}${isFinished ? " · Finalizada" : ""}`}
          onBack={goBack}
          testid="lista-detail"
        />
        <div className="max-w-2xl mx-auto px-4 md:px-8 py-6 pb-32">

          {/* Progress + totals card */}
          <div className="rounded-2xl p-4 bg-slate-900 border border-slate-800 mb-5">
            <div className="flex items-start justify-between mb-3 gap-3">
              <div>
                <div className="text-xs text-slate-400">Total estimado</div>
                <div className="text-2xl font-bold text-white">{brl(total)}</div>
              </div>
              <div className="flex flex-col sm:flex-row items-end sm:items-start gap-3 sm:gap-4 text-right">
                {checkedCount > 0 && (
                  <div>
                    <div className="text-xs text-slate-400">No carrinho</div>
                    <div className="text-xl font-bold text-amber-300">{brl(checkedTotal)}</div>
                  </div>
                )}
                <button
                  onClick={() => setShareLista(selectedLista)}
                  className="h-9 w-9 sm:h-9 sm:w-auto sm:px-3 grid place-items-center sm:flex sm:items-center sm:gap-2 rounded-xl bg-slate-800 text-slate-300 hover:text-white border border-slate-700 transition"
                  title="Compartilhar"
                >
                  <Share2 className="h-4 w-4" />
                  <span className="hidden sm:inline text-sm font-semibold">Compartilhar</span>
                </button>
              </div>
            </div>
            {items.length > 0 && (
              <>
                <div className="flex items-center justify-between text-xs text-slate-400 mb-1.5">
                  <span>{checkedCount} de {items.length} marcados</span>
                  <span>{Math.round((checkedCount / items.length) * 100)}%</span>
                </div>
                <div className="h-2 rounded-full bg-slate-800 overflow-hidden">
                  <div
                    className="h-full bg-amber-400 rounded-full transition-all duration-300"
                    style={{ width: `${(checkedCount / items.length) * 100}%` }}
                  />
                </div>
              </>
            )}
          </div>

          {/* Add item form */}
          {!isFinished && (
            <form onSubmit={handleAddItem} className="rounded-2xl bg-slate-900 border border-slate-800 p-4 mb-4 relative">
              <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">Adicionar item</div>
              <div className="flex gap-1.5">
                <div className="flex-1 min-w-0 relative">
                  <input
                    ref={itemInputRef}
                    value={itemDesc}
                    onChange={(e) => setItemDesc(e.target.value)}
                    placeholder="Nome..."
                    autoComplete="off"
                    className="w-full h-10 px-3 rounded-xl bg-slate-800 border border-slate-700 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-amber-500/40"
                  />
                  {suggestions.length > 0 && (
                    <div className="absolute top-full left-0 right-0 mt-1 bg-slate-800 border border-slate-700 rounded-xl overflow-hidden z-20 shadow-xl">
                      {suggestions.map((sug) => (
                        <button
                          key={sug.desc}
                          type="button"
                          onMouseDown={() => handleSuggestion(sug)}
                          className="w-full flex items-center justify-between px-3 py-2.5 text-sm hover:bg-slate-700 text-left transition"
                        >
                          <span className="text-white truncate">{sug.desc}</span>
                          <span className="text-slate-400 shrink-0 ml-3 text-xs">{sug.qtd}x · {brl(sug.valorUnit)}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <input
                  value={itemQtd}
                  onChange={(e) => setItemQtd(e.target.value)}
                  type="number"
                  min="0.1"
                  step="any"
                  placeholder="Qtd"
                  className="w-12 h-10 px-1.5 text-center rounded-xl bg-slate-800 border border-slate-700 text-sm text-white focus:outline-none focus:border-amber-500/40 shrink-0"
                />
                <input
                  value={itemValor}
                  onChange={(e) => setItemValor(e.target.value)}
                  type="number"
                  min="0"
                  step="any"
                  placeholder="R$"
                  className="w-16 h-10 px-1.5 text-center rounded-xl bg-slate-800 border border-slate-700 text-sm text-white focus:outline-none focus:border-amber-500/40 shrink-0"
                />
                <button
                  type="submit"
                  disabled={!itemDesc.trim()}
                  className="h-10 w-10 shrink-0 rounded-xl bg-amber-500 hover:bg-amber-400 disabled:opacity-40 grid place-items-center transition"
                >
                  <Plus className="h-5 w-5 text-slate-950" />
                </button>
              </div>
            </form>
          )}

          {/* Items list */}
          <div className="rounded-2xl bg-slate-900 border border-slate-800 overflow-hidden">
            {items.length === 0 ? (
              <div className="py-12 text-center text-slate-400 text-sm">
                <ShoppingBag className="h-8 w-8 mx-auto mb-3 opacity-30" />
                Nenhum item ainda. Adicione acima!
              </div>
            ) : (
              <div className="divide-y divide-slate-800">
                {items.map((it) => (
                  <ItemRow
                    key={it.id}
                    item={it}
                    isFinished={isFinished}
                    onToggle={() => !isFinished && updateItemLista(selectedId, it.id, { checked: !it.checked })}
                    onDelete={() => removeItemLista(selectedId, it.id)}
                    onUpdate={(patch) => updateItemLista(selectedId, it.id, patch)}
                  />
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Sticky footer */}
        {!isFinished && items.length > 0 && (
          <div className="fixed bottom-0 left-0 right-0 md:left-64 bg-slate-950/95 backdrop-blur border-t border-slate-800 px-4 py-4 z-40">
            <div className="max-w-2xl mx-auto">
              <button
                onClick={() => setShowFinalizar(true)}
                className="w-full h-12 rounded-2xl bg-emerald-500 hover:bg-emerald-400 font-semibold text-slate-950 transition flex items-center justify-center gap-2"
              >
                <CheckCircle2 className="h-5 w-5" />
                Finalizar Compra · {brl(total)}
              </button>
            </div>
          </div>
        )}

        {/* Finalizar dialog */}
        {showFinalizar && (
          <div className="fixed inset-0 z-50 grid place-items-center p-4 bg-black/60 backdrop-blur-sm">
            <div className="w-full max-w-sm bg-slate-900 border border-slate-700 rounded-2xl p-5 shadow-2xl">
              <div className="text-base font-bold mb-1">Finalizar Compra</div>
              <div className="text-sm text-slate-400 mb-4">Um gasto variável será registrado automaticamente em <span className="text-amber-300">Mercado</span>.</div>
              <div className="rounded-xl bg-slate-800 p-3 mb-4 flex items-center justify-between">
                <span className="text-sm text-slate-300 truncate">{selectedLista.nome}</span>
                <span className="font-bold text-white shrink-0 ml-3">{brl(listaTotal(selectedLista))}</span>
              </div>
              {checkedCount > 0 && checkedCount < items.length && (
                <div className="rounded-xl bg-amber-500/10 border border-amber-500/20 p-3 mb-4 text-xs text-amber-300">
                  {checkedCount} de {items.length} itens marcados. O total inclui todos os itens da lista.
                </div>
              )}
              <div className="mb-4">
                <label className="text-xs text-slate-400 mb-1.5 block">Forma de pagamento</label>
                <select
                  value={finalizarPagamento}
                  onChange={(e) => setFinalizarPagamento(e.target.value)}
                  className="w-full h-10 px-3 rounded-xl bg-slate-800 border border-slate-700 text-sm text-white focus:outline-none"
                >
                  {["Débito", "Pix", "Dinheiro", "Crédito"].map((p) => (
                    <option key={p}>{p}</option>
                  ))}
                </select>
              </div>
              <div className="flex gap-2">
                <button onClick={() => setShowFinalizar(false)} className="flex-1 h-10 rounded-xl border border-slate-700 text-sm text-slate-300 hover:bg-slate-800 transition">
                  Cancelar
                </button>
                <button onClick={handleFinalizar} className="flex-1 h-10 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-sm font-semibold text-slate-950 transition">
                  Confirmar
                </button>
              </div>
            </div>
          </div>
        )}
      </AppLayout>
    );
  }

  // ─── Overview ───────────────────────────────────────────────
  return (
    <AppLayout>
      <PageHeader title="Lista de Compras" testid="lista-compras" />
      <div className="max-w-2xl mx-auto px-4 md:px-8 py-6">

        {/* Summary + button */}
        <div className="rounded-2xl p-4 md:p-5 bg-slate-900 border border-slate-800 flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-5">
          <div>
            <div className="text-xs uppercase tracking-wider text-slate-400 font-semibold">Listas abertas</div>
            <div className="mt-1 font-display text-2xl font-bold text-white">{abertas.length}</div>
          </div>
          <div className="flex items-center gap-2 w-full md:w-auto">
            {abertas.length > 0 && (
              <button
                onClick={() => setShareLista(abertas[0])}
                className="rounded-full h-11 px-4 text-sm font-semibold inline-flex items-center justify-center gap-2 bg-slate-800 border border-slate-700 text-slate-300 hover:text-white transition w-full md:w-auto"
              >
                <Share2 className="h-4 w-4" /> Compartilhar
              </button>
            )}
            <button
              onClick={() => setShowNova(true)}
              className="monyx-btn-primary text-white rounded-full h-11 px-5 text-sm font-semibold inline-flex items-center justify-center gap-2 w-full md:w-auto"
            >
              <Plus className="h-4 w-4" /> Nova Lista
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 p-1 bg-slate-900 border border-slate-800 rounded-2xl mb-4">
          {[
            { id: "abertas", label: `Abertas (${abertas.length})` },
            { id: "finalizadas", label: `Finalizadas (${finalizadas.length})` },
          ].map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`flex-1 h-8 rounded-xl text-sm font-medium transition ${tab === t.id ? "bg-slate-800 text-white" : "text-slate-400 hover:text-white"}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* List cards */}
        <div className="space-y-3">
          {(tab === "abertas" ? abertas : finalizadas).length === 0 ? (
            <div className="rounded-2xl bg-slate-900 border border-slate-800 py-12 text-center text-slate-400 text-sm">
              <ShoppingCart className="h-8 w-8 mx-auto mb-3 opacity-30" />
              {tab === "abertas"
                ? "Nenhuma lista aberta. Clique em Nova Lista!"
                : "Nenhuma lista finalizada ainda."}
            </div>
          ) : (
            (tab === "abertas" ? abertas : finalizadas).map((lista) => (
              <div key={lista.id} className="relative group">
                <OverviewCard lista={lista} onClick={() => openDetail(lista.id)} onShare={() => setShareLista(lista)} />
                <div className="absolute top-3 right-3 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition">
                  {lista.status === "finalizada" && (
                    <button
                      onClick={(e) => { e.stopPropagation(); handleDuplicar(lista); }}
                      className="h-7 w-7 grid place-items-center rounded-lg bg-slate-800 text-slate-300 hover:text-amber-300 transition"
                      title="Duplicar lista"
                    >
                      <Copy className="h-3.5 w-3.5" />
                    </button>
                  )}
                  <button
                    onClick={(e) => { e.stopPropagation(); removeLista(lista.id); toast.success("Lista removida"); }}
                    className="h-7 w-7 grid place-items-center rounded-lg bg-slate-800 text-slate-300 hover:text-rose-300 transition"
                    title="Remover lista"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {showNova && <NovaListaDialog onClose={() => setShowNova(false)} onCreate={handleCreateLista} />}
      <ShareModal lista={shareLista} onClose={() => setShareLista(null)} />
    </AppLayout>
  );
};

export default ListaDeCompras;
